import { encode } from 'punycode';
import React, { type ReactNode, useEffect, useMemo, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { ReactComponent as LinkIcon } from '@pkg/assets/icons/v2/link-alt.svg';
import { ReactComponent as InstagramIcon } from '@pkg/assets/icons/v2/social/instagram.svg';
import { ReactComponent as TwitterIcon } from '@pkg/assets/icons/v2/social/twitter.svg';
import { useAppDispatch, useAppSelector } from '@pkg/hooks';

import { type EmojiId } from '@app/client';
import config from '@app/config';
import { stripFe0fList } from '@app/helpers';
import { loadWasms } from '@app/store/actions/emoji';
import { WasmType } from '@app/store/types/emoji';

import LinkItemButtons, { type LinkItemProps } from './LinkItemButtons/LinkItemButtons';
import './_CopyModule.scss';

interface Props {
    emojiId: EmojiId;
    iconSize?: string;
    variant?: 'primary' | 'secondary';
    directionUp?: boolean;
    directionDown?: boolean;
    directionRight?: boolean;
    directionLeft?: boolean;
    children?: ReactNode;
}
const publicUrl = config.publicUrl;
const linkPrefix = `${window.location.protocol}//`;

export function CopyModule({
    emojiId,
    iconSize,
    variant,
    directionUp,
    directionDown,
    directionRight,
    directionLeft,
    children,
}: Props) {
    const { t } = useTranslation(['copy_widget', 'common']);
    const { wasm } = useAppSelector((state) => state.emoji);
    const [showClass, setShowClass] = useState<string>('');
    const [instaLink, setInstaLink] = useState<string | undefined>();
    const [showDropdown, setShowDropdown] = useState(false);
    const [userInteraction, setUserInteraction] = useState(false);
    const hideDropdownTimeout = useRef<NodeJS.Timeout | null>(null);

    const link = `${linkPrefix}${publicUrl}/${emojiId}`; // eg. https://y.at/🖖🖖🖖
    const dispatch = useAppDispatch();

    useEffect(() => {
        dispatch(loadWasms(WasmType.EmojiWasm));
    }, [dispatch]);

    useEffect(() => {
        setShowClass(showDropdown ? 'show' : userInteraction ? 'hide' : ''); //second var to check against to make sure "hide" isn't added from the get go
    }, [showDropdown, userInteraction]);

    useEffect(() => {
        if (wasm && emojiId) {
            const instaUrl = new wasm.EmojiId(emojiId, config.maxLengthYat)?.instagramUrl();
            setInstaLink(`${linkPrefix}${publicUrl}/${instaUrl}`);
        }
    }, [wasm, emojiId]);

    const twitterLink = useMemo(() => {
        if (wasm && emojiId) {
            const wEmojiId = new wasm.EmojiId(emojiId, config.maxLengthYat);
            const cleanedEmoji = wEmojiId
                .splitCanonical()
                .map((e) => {
                    return stripFe0fList.indexOf(e) > -1 ? e.replace(new RegExp('\ufe0f', 'gi'), '') : e;
                })
                .join('');
            const punycodeEmojiId = encode(cleanedEmoji);
            return `${linkPrefix}xn--${punycodeEmojiId}.${publicUrl}`; // eg. https://xn--dz8haa.y.at
        }
        return '';
    }, [wasm, emojiId]);

    const linkItems = useMemo(() => {
        return [
            {
                link: link,
                buttonText: t('copy_link'),
                tooltipData: {},
                icon: <LinkIcon />,
            },
            {
                link: twitterLink,
                buttonText: t('copy_twitter_link'),
                tooltipData: {
                    body: t('twitter_link_body_copy'),
                    header: t('twitter_link_header'),
                },
                icon: <TwitterIcon />,
            },
            {
                link: instaLink ?? twitterLink,
                buttonText: t('copy_ig_link'),

                tooltipData: {
                    body: t('instagram_link_body_copy'),
                    header: t('instagram_link_header'),
                },
                icon: <InstagramIcon />,
            },
        ] as LinkItemProps[];
    }, [instaLink, link, twitterLink, t]);

    const mobileLinks = useMemo(() => {
        return (
            <div className="copy-module__link-container--secondary">
                {linkItems.map((item, index) => {
                    return (
                        <LinkItemButtons
                            buttonText={item.buttonText}
                            tooltipData={item.tooltipData}
                            link={item.link}
                            icon={item.icon}
                            index={index}
                            onSetShowDropDown={setShowDropdown}
                            onSetUserInteraction={setUserInteraction}
                            key={index}
                        />
                    );
                })}
            </div>
        );
    }, [linkItems]);

    if (variant === 'secondary') return <>{mobileLinks}</>;

    const directionUpClass = directionUp ? ' direction-up' : '';
    const directionRightClass = directionRight ? ' direction-right' : '';
    const directionLeftClass = directionLeft ? ' direction-left' : '';
    const directionDownClass = directionDown ? ' direction-down' : '';

    return (
        <div
            className={`copy-module ${iconSize}`}
            onMouseEnter={() => {
                const timeout = hideDropdownTimeout.current;
                if (timeout) {
                    clearTimeout(timeout);
                }
                setShowDropdown(true);
                setUserInteraction(true);
            }}
            onMouseLeave={() => {
                hideDropdownTimeout.current = setTimeout(() => {
                    setShowDropdown(false);
                    setUserInteraction(false);
                }, 200);
            }}
            onClick={() => {
                setShowDropdown(!showDropdown);
                setUserInteraction(!userInteraction);
            }}
        >
            {!children && (
                <div className="copy-module__icon-button">
                    <LinkIcon aria-label={t('alt_copy_link_icon')} />
                </div>
            )}

            {children && <div>{children}</div>}

            <div
                className={`copy-module__link-container ${showClass}${directionUpClass}${directionRightClass}${directionLeftClass}${directionDownClass}`}
            >
                {linkItems.map((item, index) => {
                    return (
                        <LinkItemButtons
                            buttonText={item.buttonText}
                            tooltipData={item.tooltipData}
                            link={item.link}
                            icon={item.icon}
                            index={index}
                            onSetShowDropDown={setShowDropdown}
                            onSetUserInteraction={setUserInteraction}
                            key={index}
                            directionUp={directionUp}
                        />
                    );
                })}
            </div>
        </div>
    );
}
